const FEET_PER_DEGREE_LAT = 364000;

const escapeXml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&apos;');

const isValidPoint = (point) => (
  Number.isFinite(Number(point?.lat)) && Number.isFinite(Number(point?.lng))
);

const formatNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n.toFixed(8).replace(/\.?0+$/, '') : '0';
};

const formatCoordinate = (point) => (
  `${formatNumber(point.lng)},${formatNumber(point.lat)},0`
);

const pointsEqual = (a, b) => (
  Math.abs(Number(a?.lat) - Number(b?.lat)) < 0.00000001
  && Math.abs(Number(a?.lng) - Number(b?.lng)) < 0.00000001
);

const closeRing = (paths) => {
  const points = (paths || []).filter(isValidPoint);
  if (points.length < 3) return [];
  const ring = [...points];
  if (!pointsEqual(ring[0], ring[ring.length - 1])) {
    ring.push(ring[0]);
  }
  return ring;
};

const ellipseToRing = (area, steps = 64) => {
  const center = area?.paths?.[0];
  if (!isValidPoint(center)) return [];

  const lat = Number(center.lat);
  const radiusXFeet = Number(area.radiusX || 0);
  const radiusYFeet = Number(area.radiusY || area.radiusX || 0);
  if (!Number.isFinite(radiusXFeet) || !Number.isFinite(radiusYFeet) || radiusXFeet <= 0 || radiusYFeet <= 0) {
    return [];
  }

  const lngFeetPerDegree = FEET_PER_DEGREE_LAT * Math.cos((lat * Math.PI) / 180);
  if (!Number.isFinite(lngFeetPerDegree) || Math.abs(lngFeetPerDegree) < 1) return [];

  const ring = [];
  for (let i = 0; i < steps; i += 1) {
    const theta = (Math.PI * 2 * i) / steps;
    ring.push({
      lat: lat + (Math.sin(theta) * radiusYFeet) / FEET_PER_DEGREE_LAT,
      lng: Number(center.lng) + (Math.cos(theta) * radiusXFeet) / lngFeetPerDegree,
    });
  }
  ring.push(ring[0]);
  return ring;
};

const styleIdForArea = (area) => `area-${String(area?.areaType || area?.geometryType || 'default').replace(/[^a-zA-Z0-9_-]/g, '-')}`;

const hexToKmlColor = (hex, alpha = '99') => {
  const clean = String(hex || '#1976d2').replace('#', '');
  if (!/^[a-fA-F0-9]{6}$/.test(clean)) return `${alpha}d27619`;
  const rr = clean.slice(0, 2);
  const gg = clean.slice(2, 4);
  const bb = clean.slice(4, 6);
  return `${alpha}${bb}${gg}${rr}`.toLowerCase();
};

const buildStyle = (area, color) => {
  const id = escapeXml(styleIdForArea(area));
  const lineColor = hexToKmlColor(area?.strokeColor || color, 'ff');
  const fillColor = hexToKmlColor(color, '66');
  return [
    `<Style id="${id}">`,
    `<LineStyle><color>${lineColor}</color><width>2</width></LineStyle>`,
    `<PolyStyle><color>${fillColor}</color></PolyStyle>`,
    `<IconStyle><color>${fillColor}</color><scale>0.8</scale></IconStyle>`,
    '</Style>',
  ].join('');
};

// Round-trip identity: stash the area's own id (and type) in ExtendedData so a
// re-imported KML can be matched back to the source area instead of duplicating
// it. Earth preserves ExtendedData across edits. Namespaced under dfl: to avoid
// colliding with anything Earth writes.
const buildExtendedData = (area) => {
  const sourceId = area?._id || area?.id;
  if (!sourceId) return '';
  return [
    '<ExtendedData>',
    `<Data name="dfl:sourceId"><value>${escapeXml(sourceId)}</value></Data>`,
    area?.areaType != null
      ? `<Data name="dfl:areaType"><value>${escapeXml(area.areaType)}</value></Data>`
      : '',
    '</ExtendedData>',
  ].filter(Boolean).join('');
};

const buildPlacemark = (area, getAreaTypeLabel, getAreaTypeColor) => {
  const paths = (area?.paths || []).filter(isValidPoint);
  const geometryType = area?.geometryType || 'polygon';
  const areaTypeLabel = getAreaTypeLabel(area?.areaType);
  const name = area?.name || areaTypeLabel || 'Area';
  const color = area?.fillColor || getAreaTypeColor(area?.areaType);
  const styleUrl = `#${styleIdForArea(area)}`;
  let geometry = '';

  if (geometryType === 'point') {
    if (!paths[0]) return null;
    geometry = `<Point><coordinates>${formatCoordinate(paths[0])}</coordinates></Point>`;
  } else if (geometryType === 'polyline') {
    if (paths.length < 2) return null;
    geometry = `<LineString><tessellate>1</tessellate><coordinates>${paths.map(formatCoordinate).join(' ')}</coordinates></LineString>`;
  } else {
    const ring = geometryType === 'ellipse' ? ellipseToRing(area) : closeRing(paths);
    if (ring.length < 4) return null;
    geometry = [
      '<Polygon><tessellate>1</tessellate><outerBoundaryIs><LinearRing><coordinates>',
      ring.map(formatCoordinate).join(' '),
      '</coordinates></LinearRing></outerBoundaryIs></Polygon>',
    ].join('');
  }

  return [
    '<Placemark>',
    `<name>${escapeXml(name)}</name>`,
    `<description>${escapeXml(areaTypeLabel || '')}</description>`,
    `<styleUrl>${escapeXml(styleUrl)}</styleUrl>`,
    buildExtendedData(area),
    geometry,
    '</Placemark>',
  ].filter(Boolean).join('');
};

export const buildSiteTakeoffKml = ({
  areas = [],
  documentName = 'Site Takeoff',
  getAreaTypeLabel,
  getAreaTypeColor,
}) => {
  const validAreas = (areas || []).filter(area => Array.isArray(area?.paths) && area.paths.length > 0);
  const stylesById = new Map();
  validAreas.forEach(area => {
    const id = styleIdForArea(area);
    if (!stylesById.has(id)) {
      const color = area?.fillColor || getAreaTypeColor(area?.areaType);
      stylesById.set(id, buildStyle(area, color));
    }
  });

  const placemarks = validAreas
    .map(area => buildPlacemark(area, getAreaTypeLabel, getAreaTypeColor))
    .filter(Boolean);

  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<kml xmlns="http://www.opengis.net/kml/2.2">',
    '<Document>',
    `<name>${escapeXml(documentName || 'Site Takeoff')}</name>`,
    ...Array.from(stylesById.values()),
    ...placemarks,
    '</Document>',
    '</kml>',
  ].join('\n');
};

export const sanitizeKmlFileName = (name) => {
  const clean = String(name || 'site-takeoff')
    .trim()
    .replace(/[^a-zA-Z0-9._-]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `${clean || 'site-takeoff'}.kml`;
};
